import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Receipt, Filter, Search, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import GlassPanel from '../../components/GlassPanel';
import EmptyState from '../../components/EmptyState';
import { usePagination } from '../../hooks/usePagination';

export function FinanceInvoices() {
  const { 
    data: invoices, 
    loading, 
    loadingMore,
    error, 
    hasMore, 
    loadMore, 
    fetchData,
    pagination
  } = usePagination('finance/{orgId}/invoices', { initialLimit: 25 });

  useEffect(() => {
    fetchData(1, false);
  }, [fetchData]);

  // Transform invoices for display
  const transformedInvoices = invoices.map(inv => ({
    id: inv.id,
    client: inv.client_name || 'Client',
    amount: inv.amount || inv.amount_gross || inv.amount_net || 0,
    status: inv.status?.toLowerCase() || 'draft',
    date: inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-GB') : 'N/A',
    dueDate: inv.due_date ? new Date(inv.due_date).toLocaleDateString('en-GB') : null
  }));

  const totalPending = transformedInvoices
    .filter(i => i.status === 'pending' || i.status === 'sent')
    .reduce((sum, i) => sum + i.amount, 0);
  const totalOverdue = transformedInvoices
    .filter(i => i.status === 'overdue')
    .reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="page">
      {/* Back Link */}
      <Link to="/finance" className="back-link">
        <ArrowLeft size={16} />
        Back to Finance
      </Link>

      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Invoice Overview</h1>
          <p>Track payment status and outstanding amounts</p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'var(--status-danger-bg)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)'
        }}>
          <AlertCircle size={18} style={{ color: 'var(--status-danger)' }} />
          <span style={{ color: 'var(--status-danger)' }}>{error}</span>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-3xl)' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Summary Cards */}
          <div className="responsive-grid-3" style={{ marginBottom: 'var(--space-xl)' }}>
            <GlassPanel>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>
                Pending
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--status-warning)' }}>
                £{totalPending.toFixed(2)}
              </div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>
                Overdue
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--status-danger)' }}>
                £{totalOverdue.toFixed(2)}
              </div>
            </GlassPanel>
            <GlassPanel>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 'var(--space-xs)' }}>
                Total Outstanding
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--text-primary)' }}>
                £{(totalPending + totalOverdue).toFixed(2)}
              </div>
            </GlassPanel>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
            <div style={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)',
              padding: 'var(--space-sm) var(--space-md)',
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)'
            }}>
              <Search size={16} style={{ color: 'var(--text-dimmed)' }} />
              <input 
                type="text" 
                placeholder="Search invoices..."
                style={{ 
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />
            </div>
            <button className="btn btn-secondary">
              <Filter size={16} />
              Filter
            </button>
          </div>

          {/* Invoice List */}
          <GlassPanel title="All Invoices" titleIcon={Receipt}>
            {transformedInvoices.length > 0 ? (
              <>
                <div className="list-container" data-testid="finance-invoices-list">
                  {transformedInvoices.map((invoice) => (
                    <Link key={invoice.id} to={`/invoices/${invoice.id}`} className="list-item" data-testid={`finance-invoice-item-${invoice.id}`}>
                      <div className="list-item-content">
                        <div className="list-item-title">Invoice #{invoice.id?.slice(-4) || '0000'}</div>
                        <div className="list-item-subtitle">{invoice.client} • {invoice.date}</div>
                      </div>
                      <div className="list-item-meta">
                        <span className={`status-badge ${invoice.status}`}>
                          {invoice.status}
                        </span>
                        <span className="list-item-amount">£{invoice.amount.toFixed(2)}</span>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginTop: 'var(--space-lg)',
                    paddingTop: 'var(--space-lg)',
                    borderTop: '1px solid var(--glass-border)'
                  }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="btn btn-secondary"
                      data-testid="load-more-finance-invoices"
                      style={{ minWidth: '200px' }}
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} />
                          Load More ({pagination.total - transformedInvoices.length} remaining)
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Pagination Info */}
                <div style={{ 
                  textAlign: 'center', 
                  marginTop: 'var(--space-md)',
                  fontSize: '0.75rem',
                  color: 'var(--text-muted)'
                }}>
                  Showing {transformedInvoices.length} of {pagination.total} invoices
                </div>
              </>
            ) : (
              <EmptyState 
                icon={Receipt}
                title="No invoices"
                description="Create your first invoice to get started"
              />
            )}
          </GlassPanel>
        </>
      )}
    </div>
  );
}

export default FinanceInvoices;
